import React from "react";
import { createPortal } from "react-dom";
import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import DOMPurify from 'dompurify';
import {
  Edit,
  Trash2,
  Check,
  X,
  Share2,
  ChevronDown,
  Pencil,
  Loader2,
  Play,
  Pause,
  MoreHorizontal,
  GitBranch,
  FileDown,
  FileText,
  Bot,
  Globe,
  Atom,
} from "lucide-react";

import { Button } from "@/components/ui/button";

import {
  Clipboard as ClipboardIcon,
  VolumeIcon,
  EditIcon,
} from "../../packages/svgs";
import CreateImageIcon from "../../packages/svgs/CreateImageIcon.tsx";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faThumbsUp as farThumbsUp,
  faThumbsDown as farThumbsDown,
} from "@fortawesome/free-regular-svg-icons";
import {
  faThumbsUp as fasThumbsUp,
  faThumbsDown as fasThumbsDown,
} from "@fortawesome/free-solid-svg-icons";

import {
  PerplexityIcon,
  KimiIcon,
  GoogleAIIcon,
  ClaudeIcon,
  GPTIcon,
  GeminiIcon,
  LlamaIcon,
  DeepseekIcon,
  QwenIcon,
} from "./icons";
import { useToast } from "@/hooks/use-toast";
import { ParallelSearchResponse } from "@/components/ParallelSearchResponse";
import { SearchResultsCard, type SearchResponse } from "@/components/agents/SearchResultsCard";
import { FlowChartRenderer } from "@/components/agents/FlowChartRenderer";
import { CalculatorCard } from "@/components/agents/CalculatorCard";
import { TranslatorCard } from "@/components/agents/TranslatorCard";
import { UnitConverterCard } from "@/components/agents/UnitConverterCard";
import { AttachedMedia, ALL_AGENTS } from "@/types/aiAgents";
import { WebScreenshot } from "./WebScreenshot";
import { ScreenshotModal } from "./ScreenshotModal";
import { FormattedAIResponse } from "./FormattedAIResponse";
import { generateSpeechStream } from "@/services/textToSpeechService";
import { LazyImage } from "./LazyImage";
import { SearchResults, SearchSourcesButton } from "@/components/search/SearchResults";
import { GoogleAIRichResults } from "./search/GoogleAIRichResults";
import { SearchResult } from "@/types/searchResults";
import { type ImageModalData } from "@/pages/Index";
import WeatherCard from "./WeatherCard";
import AudioManager from "@/utils/audioManager";
import { MemeRenderer } from "./meme/MemeComponents";
import { ColorPaletteResponse } from "./agents/ColorPaletteResponse";
import { LARAResultsRenderer } from './agents/LARAResultsRenderer';
import { VGenVideoStatus } from './agents/VGenVideoStatus';
import { WikipediaCard } from "./agents/WikipediaCard";
import { VideoSummaryCard, VideoSummaryResult as VSResult } from "./agents/VideoSummaryCard";
import { YouTubeResults } from "./agents/YouTubeResults";
import { GiphyResults } from "./agents/GiphyResults";
import { PasswordGeneratorCard } from "./agents/PasswordGeneratorCard";
import { QRCodeGeneratorCard } from "./agents/QRCodeGeneratorCard";
import { PPTMakerViewer } from "./agents/PPTMakerViewer";
import { MiniAudioBar } from "@/components/ui/MiniAudioBar";
import { QuizCardStack } from "@/components/quiz/QuizCardStack";
import { useNavigate } from "react-router-dom";
import chatgptLight from "@/assets/chatgpt_light.png";
import chatgptDark from "@/assets/chatgpt_dark.png";
import deepseekLogo from "@/assets/deepseek.png";
import claudeLogo from "@/assets/claude.png";
import geminiLogo from "@/assets/gemini.png";
import llamaLogo from "@/assets/llama.png";
import qwenLogo from "@/assets/qwen.png";
import ogaiLogo from "/image.png";
import { storage, AppSettings } from "@/utils/storageManager";
import { getOptimizedCloudinaryUrl } from "@/utils/cloudinary";

const escapeMarkdownTableCell = (value: unknown): string => {
  const text = typeof value === 'string' ? value : value == null ? '' : String(value);
  return text
    .replace(/\r\n/g, '\n')
    .replace(/\n/g, ' ')
    .replace(/\|/g, '\\|')
    .trim();
};

const googleAITableToMarkdown = (table: unknown): string => {
  if (!Array.isArray(table) || table.length === 0) return '';
  const rows = table.filter((r) => Array.isArray(r)) as unknown[][];
  if (rows.length === 0) return '';

  const headerRow = rows[0] ?? [];
  const colCount = (headerRow as unknown[]).length || Math.max(...rows.map((r) => r.length));
  if (!colCount) return '';

  const normalizeRow = (r: unknown[]): string[] => {
    const out: string[] = [];
    for (let i = 0; i < colCount; i++) {
      out.push(escapeMarkdownTableCell(r?.[i] ?? ''));
    }
    return out;
  };

  const headers = normalizeRow(headerRow as unknown[]);
  const md: string[] = [];
  md.push(`| ${headers.join(' | ')} |`);
  md.push(`| ${headers.map(() => '---').join(' | ')} |`);

  for (let i = 1; i < rows.length; i++) {
    const row = normalizeRow(rows[i] ?? []);
    md.push(`| ${row.join(' | ')} |`);
  }

  return `\n\n${md.join('\n')}\n\n`;
};

const googleAITextBlocksToMarkdown = (textBlocks: unknown): string => {
  if (!Array.isArray(textBlocks)) return '';
  const parts: string[] = [];

  for (const block of textBlocks as any[]) {
    if (!block || typeof block !== 'object') continue;
    const type = String(block.type || '').toLowerCase();

    if (type === 'paragraph' && typeof block.snippet === 'string') {
      const t = block.snippet.trim();
      if (t) parts.push(t);
      continue;
    }

    if (type === 'table') {
      const mdTable = googleAITableToMarkdown(block.table);
      if (mdTable) parts.push(mdTable);
      continue;
    }

    if (typeof block.snippet === 'string') {
      const t = block.snippet.trim();
      if (t) parts.push(t);
      continue;
    }

    if (typeof block.text === 'string') {
      const t = block.text.trim();
      if (t) parts.push(t);
      continue;
    }
  }

  return parts.join('\n\n').trim();
};

// Helper function to get proper display names for AI models and agents
const getDisplayName = (model: string, agentName?: string): string => {
  // If agentName exists and doesn't contain "suffix" or other generic terms, use it
  if (agentName && !agentName.toLowerCase().includes('suffix') && !agentName.toLowerCase().includes('agent')) {
    return agentName;
  }

  // Map model names to proper display names
  const modelDisplayNames: { [key: string]: string } = {
    'gpt': 'ChatGPT',
    'chat gpt': 'ChatGPT',
    'chatgpt': 'ChatGPT',
    'claude': 'Claude',
    'gemini': 'Gemini',
    'llama': 'LLaMA',
    'deepseek': 'DeepSeek',
    'qwen': 'Qwen',
    'perplexity': 'Perplexity',
    'google ai': 'Google AI',
    'googleai': 'Google AI',
    'gpt-oss': 'GPT-OSS',
    'color-palette': 'Color Palette',
    'pixabay': 'Pixabay',
    'weather': 'Weather',
    'web-search': 'Web Search',
    'web search': 'Web Search',
    'lara': 'LARA'
  };

  return modelDisplayNames[model?.toLowerCase()] || model || 'AI Assistant';
};

// Helper to map stored model ids to their logo URLs (same set as ModelSelector)
const getModelLogo = (model?: string, isDarkMode?: boolean): string | undefined => {
  const key = model?.toLowerCase();
  switch (key) {
    case 'gpt':
      return isDarkMode ? chatgptLight : chatgptDark;
    case 'chat gpt':
      return isDarkMode ? chatgptLight : chatgptDark;
    case 'chatgpt':
      return isDarkMode ? chatgptLight : chatgptDark;
    case 'glm':
      return isDarkMode ? chatgptLight : chatgptDark;
    case 'deepseek':
      return deepseekLogo;
    case 'claude':
      return claudeLogo;
    case 'gemini':
      return geminiLogo;
    case 'llama':
      return llamaLogo;
    case 'qwen':
      return qwenLogo;
    case 'lara':
      return '/ai-svg-oreog.svg';
    case 'sambanova':
    case 'cerebras':
    case 'upstage':
      return ogaiLogo;
    default:
      return undefined;
  }
};

interface Message {
  id: number;
  content: string;
  type: "user" | "ai";
  timestamp: Date;
  model: string;
  displayModel?: string;
  isImage?: boolean;
  isHTML?: boolean;
  attachedMedia?: AttachedMedia;
  attachedMediaList?: AttachedMedia[];
  selectedAgent?: any;
  agentData?: any;
  reasoning?: string;
  reasoning_content?: string; // optional reasoning property from ChatGPT responses
  isWebSearchMode?: boolean;
  searchResults?: SearchResult;
  weatherData?: any;
  laraResults?: any[]; // For LARA multi-agent results
  isLARA?: boolean; // Flag for LARA responses
  isStreaming?: boolean;
  parallelSources?: any[];
  branchedFrom?: {
    chatId: string | null;
    messageId: number;
    summary?: string;
  };
  // Multi-AI grouping metadata (optional)
  multiBatchId?: string;
  multiOrder?: number;
}

interface ChatMessageProps {
  message: Message;
  isDarkMode: boolean;
  isWebSearchMode?: boolean;
  isImageMode?: boolean;
  onRegenerate?: () => void;
  onShare: () => void;
  onEdit?: (content: string) => void;
  isStreaming?: boolean;
  onStreamComplete?: () => void;
  onBranch?: (messageId: number) => void;
  onAskOtherAI?: (aiMessageId: number, selectedModel: string) => void;
  hideActions?: boolean; // Hide all action buttons (for shared/read-only views)
}

export const ChatMessage = ({
  message,
  isDarkMode,
  isWebSearchMode,
  isImageMode,
  onRegenerate,
  onShare,
  onEdit,
  isStreaming = false,
  onStreamComplete,
  onBranch,
  onAskOtherAI,
  hideActions = false,
}: ChatMessageProps) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [urls, setUrls] = useState<string[]>([]);
  const [showReasoning, setShowReasoning] = useState<boolean>(false);
  const [isUserExpanded, setIsUserExpanded] = useState(false);
  const [needsUserTruncation, setNeedsUserTruncation] = useState(false);
  const userContentRef = useRef<HTMLDivElement>(null);
  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    imageUrl: string;
    title: string;
  }>({
    isOpen: false,
    imageUrl: "",
    title: "",
  });
  const [reaction, setReaction] = useState<"like" | "dislike" | null>(null);
  const [showLike, setShowLike] = useState(true);
  const [showDislike, setShowDislike] = useState(true);
  const [copiedMessageId, setCopiedMessageId] = useState<number | null>(null);
  const [isCopying, setIsCopying] = useState(false);
  const [isPlayingAudio, setIsPlayingAudio] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const copyTimeoutRef = useRef<NodeJS.Timeout>();
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [showMoreDropdown, setShowMoreDropdown] = useState(false);
  const [isBranching, setIsBranching] = useState(false);
  const [showAskOtherAI, setShowAskOtherAI] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const askAIDropdownRef = useRef<HTMLDivElement>(null);
  const moreAnchorRef = useRef<HTMLDivElement>(null);
  const morePlaceTopRef = useRef<boolean>(false);
  const askPlaceTopRef = useRef<boolean>(false);
  const [morePlaceTop, setMorePlaceTop] = useState(false);
  const [askPlaceTop, setAskPlaceTop] = useState(false);
  const [askCoords, setAskCoords] = useState<{ top: number; left: number; width: number; height: number }>({ top: 0, left: 0, width: 0, height: 0 });
  const [moreCoords, setMoreCoords] = useState<{ top: number; left: number; width: number; height: number }>({ top: 0, left: 0, width: 0, height: 0 });
  const [moreReady, setMoreReady] = useState(false);
  const [exclusiveAskHover, setExclusiveAskHover] = useState<string | null>(null);
  const exclusiveAskPanelRef = useRef<HTMLDivElement | null>(null);
  const [exclusiveAskSticky, setExclusiveAskSticky] = useState(false);
  const [backgroundType, setBackgroundType] = useState<'solid' | 'image'>(() => {
    try {
      const settings = storage.getAppSettings() as AppSettings;
      const bt = (settings?.backgroundType as 'solid' | 'image' | undefined) ?? 'solid';
      return bt === 'image' ? 'image' : 'solid';
    } catch {
      return 'solid';
    }
  });

  const derivedReasoning = useMemo(() => {
    let reason: string | undefined = (message.reasoning as any) || (message as any).reasoning_content;
    if (!reason && typeof message.content === 'string') {
      const thinkMatch = message.content.match(/<think>([\s\S]*?)<\/think>/i);
      if (thinkMatch && thinkMatch[1]) {
        reason = thinkMatch[1].trim();
      }
    }
    return reason;
  }, [message.reasoning, message.content]);

  const citationSources = useMemo(() => {
    if (message.searchResults?.results) {
      return message.searchResults.results.map(r => ({
        title: r.title,
        link: r.url,
        snippet: r.content,
        source: new URL(r.url).hostname.replace(/^www\./, '')
      }));
    }
    // Fallback: Check for references in content JSON if applicable
    try {
      if (typeof message.content === 'string' && message.content.trim().startsWith('{')) {
        const data = JSON.parse(message.content);
        if (data.references && Array.isArray(data.references)) {
          return data.references.map((r: any) => ({
            title: r.title,
            link: r.link || r.url,
            snippet: r.snippet,
            source: r.source
          }));
        }
      }
    } catch { }
    return undefined;
  }, [message.searchResults, message.content]);

  useEffect(() => {
    const handleAppSettingsChange = (e: CustomEvent) => {
      const nextType = (e as any).detail?.backgroundType;
      if (nextType === 'solid' || nextType === 'image') {
        setBackgroundType(nextType);
      } else {
        setBackgroundType('solid');
      }
    };

    window.addEventListener('appSettingsChanged', handleAppSettingsChange as EventListener);
    return () => {
      window.removeEventListener('appSettingsChanged', handleAppSettingsChange as EventListener);
    };
  }, []);

  useEffect(() => {
    setIsUserExpanded(false);
  }, [message.content]);

  useEffect(() => {
    const el = userContentRef.current;
    if (!el || isUserExpanded || message.type !== 'user') return;

    const checkOverflow = () => {
      // Check if the scrollHeight is greater than 150px
      setNeedsUserTruncation(el.scrollHeight > 150);
    };

    const t = setTimeout(checkOverflow, 50);
    window.addEventListener('resize', checkOverflow);
    return () => {
      clearTimeout(t);
      window.removeEventListener('resize', checkOverflow);
    };
  }, [message.content, isUserExpanded, message.type]);

  useEffect(() => {
    // Extract URLs from Google search results
    if (
      message.selectedAgent?.name === "Google Search" &&
      message.type === "ai"
    ) {
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = message.content;
      const searchResults = tempDiv.querySelectorAll(".search-result");
      const foundUrls = Array.from(searchResults)
        .map((result) => result.getAttribute("data-url"))
        .filter(Boolean) as string[];
      setUrls(foundUrls);
    }

    // Listen for screenshot modal events
    const handleScreenshotModal = (
      event: CustomEvent<{ url: string; title: string }>
    ) => {
      setModalState({
        isOpen: false,
        imageUrl: event.detail.url,
        title: event.detail.title,
      });
    };

    window.addEventListener(
      "openScreenshotModal",
      handleScreenshotModal as EventListener
    );
    return () => {
      window.removeEventListener(
        "openScreenshotModal",
        handleScreenshotModal as EventListener
      );
    };
  }, [message]);

  useEffect(() => {
    return () => {
      // Clear any pending timeouts when component unmounts
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
    };
  }, []);

  // Handle click outside dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowMoreDropdown(false);
      }
      if (askAIDropdownRef.current && !askAIDropdownRef.current.contains(event.target as Node)) {
        setShowAskOtherAI(false);
      }
    };

    if (showMoreDropdown || showAskOtherAI) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMoreDropdown, showAskOtherAI]);

  // Auto position and scroll into view when menus open
  useEffect(() => {
    const adjustMenu = (el: HTMLDivElement | null, setTop: (v: boolean) => void) => {
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight;
      // Flip above if bottom overflows
      if (rect.bottom > vh - 8) {
        setTop(true);
      } else if (rect.top < 8) {
        // If top overflows, prefer bottom placement
        setTop(false);
      }
      // After deciding placement, ensure visibility by scrolling if still clipped
      const finalRect = el.getBoundingClientRect();
      let delta = 0;
      if (finalRect.bottom > vh) delta = finalRect.bottom - vh + 12;
      if (finalRect.top < 0) delta = finalRect.top - 12;
      if (delta !== 0) {
        window.scrollBy({ top: delta, behavior: 'smooth' });
      }
    };

    // Wait for DOM paint
    const id = requestAnimationFrame(() => {
      if (showMoreDropdown) {
        const anchor = moreAnchorRef.current;
        if (anchor) {
          const ar = anchor.getBoundingClientRect();
          // Step 1: mount offscreen and hidden, then measure next frame
          setMoreReady(false);
          const width = 192; // w-48
          setMoreCoords({ top: -9999, left: -9999, width, height: 0 });

          // Step 2: after it's mounted, measure and set final coords, then reveal
          requestAnimationFrame(() => {
            const dd = dropdownRef.current;
            if (!dd) return;
            const r = dd.getBoundingClientRect();
            const measuredHeight = Math.max(160, Math.round(r.height));
            const gap = 8;
            const gapAbove = 4; // tighter when above the buttons
            const placeTop = ar.bottom + measuredHeight + gap > window.innerHeight;
            setMorePlaceTop(placeTop);
            const top = placeTop
              ? Math.max(8, ar.top - measuredHeight - gapAbove)
              : Math.min(window.innerHeight - gap, ar.bottom + gap);
            const left = Math.min(window.innerWidth - gap - width, Math.max(gap, ar.right - width));
            setMoreCoords({ top, left, width, height: measuredHeight });
            // Reveal after coords are final
            requestAnimationFrame(() => setMoreReady(true));
          });
        }

      }
      if (showAskOtherAI) {
        // Compute placement relative to More button anchor for portal positioning
        const anchor = moreAnchorRef.current;
        if (anchor) {
          const ar = anchor.getBoundingClientRect();
          // Default place below; if not enough space, place above
          const dropdownHeight = 280; // approx height; will correct after first render if needed
          const gap = 8;
          const placeTop = ar.bottom + dropdownHeight + gap > window.innerHeight;
          setAskPlaceTop(placeTop);
          const top = placeTop ? Math.max(8, ar.top - dropdownHeight - gap) : Math.min(window.innerHeight - gap, ar.bottom + gap);
          // Align right edge with anchor's right edge, width ~ 224px
          const width = 224;
          const left = Math.min(window.innerWidth - gap - width, Math.max(gap, ar.right - width));
          setAskCoords({ top, left, width, height: dropdownHeight });
        }
      }
    });
    return () => cancelAnimationFrame(id);
  }, [showMoreDropdown, showAskOtherAI]);

  // Recompute on resize/scroll while Ask Other AI is open
  useEffect(() => {
    if (!showAskOtherAI) return;
    const handler = () => {
      const anchor = moreAnchorRef.current;
      if (!anchor) return;
      const ar = anchor.getBoundingClientRect();
      const dropdownHeight = askCoords.height || 280;
      const gap = 8;
      const placeTop = ar.bottom + dropdownHeight + gap > window.innerHeight;
      setAskPlaceTop(placeTop);
      const top = placeTop ? Math.max(8, ar.top - dropdownHeight - gap) : Math.min(window.innerHeight - gap, ar.bottom + gap);
      const width = askCoords.width || 224;
      const left = Math.min(window.innerWidth - gap - width, Math.max(gap, ar.right - width));
      setAskCoords({ top, left, width, height: dropdownHeight });
    };
    window.addEventListener('resize', handler);
    window.addEventListener('scroll', handler, true);
    return () => {
      window.removeEventListener('resize', handler);
      window.removeEventListener('scroll', handler, true);
    };
  }, [showAskOtherAI, askCoords.height, askCoords.width]);

  // Keep Claude panel (Ask Other AI) open until outside click/tap
  useEffect(() => {
    if (exclusiveAskHover !== 'claude') return;
    const handleDown = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node | null;
      if (!target) return;
      if (exclusiveAskPanelRef.current && exclusiveAskPanelRef.current.contains(target)) return;
      setExclusiveAskHover(null);
      setExclusiveAskSticky(false);
    };
    document.addEventListener('mousedown', handleDown);
    document.addEventListener('touchstart', handleDown, { passive: true });
    return () => {
      document.removeEventListener('mousedown', handleDown);
      document.removeEventListener('touchstart', handleDown);
    };
  }, [exclusiveAskHover]);

  // Recompute on resize/scroll while More menu is open
  useEffect(() => {
    if (!showMoreDropdown) return;
    const handler = () => {
      const anchor = moreAnchorRef.current;
      if (!anchor) return;
      const ar = anchor.getBoundingClientRect();
      const dropdownHeight = moreCoords.height || dropdownRef.current?.getBoundingClientRect().height || 240;
      const gap = 8;
      const gapAbove = 4; // tighter when above
      const placeTop = ar.bottom + dropdownHeight + gap > window.innerHeight;
      setMorePlaceTop(placeTop);
      const top = placeTop
        ? Math.max(8, ar.top - dropdownHeight - gapAbove)
        : Math.min(window.innerHeight - gap, ar.bottom + gap);
      const width = moreCoords.width || 192;
      const left = Math.min(window.innerWidth - gap - width, Math.max(gap, ar.right - width));
      setMoreCoords({ top, left, width, height: dropdownHeight });
    };
    window.addEventListener('resize', handler);
    window.addEventListener('scroll', handler, true);
    return () => {
      window.removeEventListener('resize', handler);
      window.removeEventListener('scroll', handler, true);
    };
  }, [showMoreDropdown, moreCoords.height, moreCoords.width]);

  const copyToClipboard = (text: string, messageId: number) => {
    navigator.clipboard.writeText(text);

    // Clear any existing timeout
    if (copyTimeoutRef.current) {
      clearTimeout(copyTimeoutRef.current);
    }

    // Set the new copied message ID
    setCopiedMessageId(messageId);

    // Clear the copied state after 2 seconds
    copyTimeoutRef.current = setTimeout(() => {
      setCopiedMessageId(null);
    }, 2000);
  };

  const handleCopyClick = async () => {
    try {
      if (message.isImage && typeof message.content === 'string') {
        setIsCopying(true);
        try {
          const response = await fetch(message.content, { mode: 'cors' });
          const blob = await response.blob();
          // Prefer original mime type, fallback to png
          const mime = blob.type || 'image/png';
          const item = new ClipboardItem({ [mime]: blob });
          await navigator.clipboard.write([item]);

          // success feedback mirrors text copy flow
          if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
          setCopiedMessageId(message.id);
          copyTimeoutRef.current = setTimeout(() => setCopiedMessageId(null), 2000);
        } catch (err) {
          // Fallback: copy URL
          await navigator.clipboard.writeText(message.content);
          if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
          setCopiedMessageId(message.id);
          copyTimeoutRef.current = setTimeout(() => setCopiedMessageId(null), 2000);
        } finally {
          setIsCopying(false);
        }
      } else {
        // Non-image copy as plain text
        const contentString = typeof message.content === 'string' ? message.content : JSON.stringify(message.content);
        copyToClipboard(contentString, message.id);
      }
    } catch (e) {
      console.error('Copy failed:', e);
      toast({ title: 'Copy failed', description: 'Could not copy to clipboard', variant: 'destructive' });
      setIsCopying(false);
    }
  };

  const shareMessage = async () => {
    try {
      const contentString = typeof message.content === 'string' ? message.content : JSON.stringify(message.content);
      const textContent = contentString.includes("<")
        ? extractTextContent(contentString)
        : contentString;

      if (navigator.share) {
        await navigator.share({
          text: textContent,
          title: "AI Response",
        });
      } else {
        await navigator.clipboard.writeText(textContent);
        toast({
          title: "Copied to clipboard",
          description: "Message text copied to clipboard for sharing",
        });
      }
    } catch (err) {
      console.error("Share failed:", err);
      toast({
        title: "Share failed",
        description: "Could not share message",
        variant: "destructive",
      });
    }
  };

  const [isGeneratingAudio, setIsGeneratingAudio] = useState(false);
  const displayModelName = message.displayModel || message.model;

  // Helper to strip HTML to plain text for TTS
  const extractTextContent = (htmlContent: string): string => {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = htmlContent;
    return tempDiv.textContent || tempDiv.innerText || "";
  };

  const cleanTextForTTS = (text: string): string => {
    // Replace code blocks with placeholder
    let cleaned = text.replace(/```[\s\S]*?```/g, " you can see the code in our conversation ");

    // Remove base64 image data
    cleaned = cleaned.replace(/data:image\/[a-z]+;base64,[a-zA-Z0-9+/=]+/gi, "");

    // Limit repeated signs (non-alphanumeric) to max 3 (e.g. ****** -> ***)
    cleaned = cleaned.replace(/([^a-zA-Z0-9\s])\1{3,}/g, "$1$1$1");

    // Limit hyphens to max 2 (e.g. ---------- -> --)
    cleaned = cleaned.replace(/-{3,}/g, "--");

    // Remove double newlines/gaps
    cleaned = cleaned.replace(/\n\s*\n/g, "\n");

    return cleaned;
  };

  const handleListen = async () => {
    const audioManager = AudioManager.getInstance();

    // If this is an image-only response, bail
    if (message.isImage) {
      toast({
        title: "Audio unavailable",
        description: "This response is an image-only message",
      });
      return;
    }

    // If this message is already playing, pause it
    if (audioManager.isPlaying(message.id)) {
      audioManager.pauseAudio();
      setIsPlayingAudio(false);
      return;
    }

    // If paused, resume
    if (audioManager.isPaused(message.id)) {
      audioManager.resumeAudio();
      setIsPlayingAudio(true);
      return;
    }

    // If paused, resume
    if (audioManager.isPaused(message.id)) {
      audioManager.resumeAudio();
      setIsPlayingAudio(true);
      return;
    }

    // Close any open action dropdown when play is pressed
    setShowMoreDropdown(false);

    // Show mini player instantly with a preparing state for this message
    try {
      window.dispatchEvent(
        new CustomEvent("audioManagerUpdate", {
          detail: {
            action: "preparing",
            messageId: message.id,
            isPlaying: false,
            currentTime: 0,
            duration: 0,
          },
        })
      );
    } catch { }

    // Stop any currently playing audio immediately so loader shows cleanly
    try {
      audioManager.stopAudio();
      window.dispatchEvent(new Event("closeGlobalAudioBar"));
    } catch { }

    try {
      // First, try cache
      const cached = audioManager.getCachedUrl(message.id);
      if (cached) {
        await audioManager.playAudio(message.id, cached, (isPlaying) => {
          setIsPlayingAudio(isPlaying);
        });

        const activeEl = audioManager.getCurrentAudio();
        if (activeEl) {
          try {
            window.dispatchEvent(
              new CustomEvent("openGlobalAudioBar", {
                detail: { audioElement: activeEl, title: `AI message #${message.id}` },
              })
            );
          } catch { }
        }
        return;
      }

      setIsGeneratingAudio(true);
      const contentString = typeof message.content === 'string' ? message.content : JSON.stringify(message.content);
      let textContent = contentString.includes("<")
        ? extractTextContent(contentString)
        : contentString;

      textContent = cleanTextForTTS(textContent);

      if (!textContent.trim()) {
        throw new Error("No text content to convert to speech");
      }

      // Generate audio URL
      const audio = await generateSpeechStream(textContent, { provider: 'unrealspeech' });
      const audioUrl = audio.src;
      setIsGeneratingAudio(false);

      // Cache for replays
      audioManager.setCachedUrl(message.id, audioUrl);

      // Use audio manager to play
      await audioManager.playAudio(message.id, audioUrl, (isPlaying) => {
        setIsPlayingAudio(isPlaying);
      });

      // After play starts, sync GlobalAudioBar with the real audio element
      const activeEl = audioManager.getCurrentAudio();
      if (activeEl) {
        try {
          window.dispatchEvent(
            new CustomEvent("openGlobalAudioBar", {
              detail: { audioElement: activeEl, title: `AI message #${message.id}` },
            })
          );
        } catch { }
      }
    } catch (err) {
      console.error("TTS failed:", err);
      setIsGeneratingAudio(false);
      setIsPlayingAudio(false);
      toast({
        title: "Text-to-Speech Failed",
        description: "Could not convert message to speech",
        variant: "destructive",
      });
    }
  };



  // Create a print-friendly window and trigger browser's Save as PDF
  const handleOpenAsPDF = useCallback(() => {
    try {
      // Get message content string and strip <think>...</think>
      const contentString = typeof message.content === 'string' ? message.content : JSON.stringify(message.content ?? '');
      const withoutThink = contentString.replace(/<think>[\s\S]*?<\/think>/gi, '').trim();

      // Decide whether to treat as HTML or plain/markdown
      const looksLikeHtml = /<\w+[^>]*>/.test(withoutThink);

      // Minimal HTML escaping
      const escapeHtml = (s: string) => s
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');

      // Very light markdown to HTML (headings, bold/italic, code, lists, links)
      const mdToHtml = (md: string) => {
        let h = md;
        // Code blocks
        h = h.replace(/```[\s\S]*?```/g, (m) => `<pre><code>${escapeHtml(m.replace(/```/g, '').trim())}</code></pre>`);
        // Inline code
        h = h.replace(/`([^`]+)`/g, '<code>$1</code>');
        // Bold and italic
        h = h.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
        h = h.replace(/\*([^*]+)\*/g, '<em>$1</em>');
        // Headings
        h = h.replace(/^######\s+(.+)$/gm, '<h6>$1</h6>')
          .replace(/^#####\s+(.+)$/gm, '<h5>$1</h5>')
          .replace(/^####\s+(.+)$/gm, '<h4>$1</h4>')
          .replace(/^###\s+(.+)$/gm, '<h3>$1</h3>')
          .replace(/^##\s+(.+)$/gm, '<h2>$1</h2>')
          .replace(/^#\s+(.+)$/gm, '<h1>$1</h1>');
        // Links
        h = h.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');
        // Lists (very basic)
        h = h.replace(/^(\s*)-\s+(.+)$/gm, '$1• $2');
        // Line breaks
        h = h.replace(/\n\n/g, '</p><p>').replace(/\n/g, '<br/>');
        return `<p>${h}</p>`;
      };

      const bodyHtml = looksLikeHtml
        ? withoutThink
        : mdToHtml(escapeHtml(withoutThink));

      const titleHtml = `OREOG : ${getDisplayName(message.model, message.selectedAgent?.name).toUpperCase()}`;
      const title = `<img src="https://res.cloudinary.com/dx0aqgtyg/image/upload/image_wja3qz.png" alt="OREOG" height="30" width="30" style="vertical-align: middle; margin-right: 8px" />${titleHtml}`;

      const styles = `
        <style>
          body { font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, "Apple Color Emoji", "Segoe UI Emoji"; margin: 24px; color: #111827; }
          h1,h2,h3,h4 { margin: 0.8em 0 0.4em; }
          p { line-height: 1.6; margin: 0 0 1em; }
          pre { background: #f3f4f6; padding: 12px; border-radius: 8px; overflow: auto; }
          code { background: #f3f4f6; padding: 2px 4px; border-radius: 4px; }
          a { color: #2563eb; text-decoration: underline; }
          header { margin-bottom: 16px; padding-bottom: 12px; border-bottom: 1px solid #e5e7eb; display:flex; align-items:center; justify-content:space-between; gap:12px; }
          .meta { color: #6b7280; font-size: 12px; }
          .header-left { display:flex; flex-direction:column; gap:4px; }
          .print-btn { appearance: none; border: 1px solid #e5e7eb; background: #ffffff; color: #111827; font-size: 12px; padding: 6px 10px; border-radius: 16px; cursor: pointer; display:inline-flex; align-items:center; gap:8px; }
          .print-btn:hover { background: #f9fafb; }
          .print-btn:active { background: #f3f4f6; }
          @media print {
            body { color: #000; }
            a { color: #000; text-decoration: none; }
            .print-btn { display: none; }
          }
        </style>
      `;

      const html = `<!doctype html>
        <html>
          <head>
            <meta charset="utf-8" />
            <meta name="viewport" content="width=device-width, initial-scale=1" />
            <title>${titleHtml}</title>
            <link rel="icon" href="/favivo.ico" type="image/png">
            ${styles}
          </head>
          <body>
            <header>
              <div class="header-left">
                <div style="font-weight:600; font-size:18px;">${title}</div>
                <div class="meta">${formatTimestamp(message.timestamp)}</div>
              </div>
              <button class="print-btn" onclick="window.print()" aria-label="Print this page">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <path d="M6 9V3h12v6" stroke="#111827" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M6 17H4a2 2 0 0 1-2-2v-3a3 3 0 0 1 3-3h14a3 3 0 0 1 3 3v3a2 2 0 0 1-2 2h-2" stroke="#111827" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M6 13h12v8H6z" stroke="#111827" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M8 15h8M8 19h8" stroke="#111827" stroke-width="1.5" stroke-linecap="round"/>
                </svg>
                Print
              </button>
            </header>
            <main>${bodyHtml}</main>
          </body>
        </html>`;

      const win = window.open('', '_blank');
      if (!win) return;
      win.document.open();
      win.document.write(html);
      win.document.close();
      // Do not auto-open print dialog; user can click the Print button in the header
    } catch (e) {
      console.error('Open as PDF failed:', e);
      toast({ title: 'Failed to open PDF', description: 'Could not prepare the message for printing', variant: 'destructive' });
    }
  }, [message, isDarkMode]);

  const handleOpenInDoc = useCallback(() => {
    try {
      setShowMoreDropdown(false);
      navigate('/doc', { state: { message, isDarkMode } });
    } catch (e) {
      console.error('Open in Doc failed:', e);
      toast({ title: 'Failed to open', description: 'Could not open the document view', variant: 'destructive' });
    }
  }, [navigate, message, isDarkMode]);

  const handleImageClick = (
    imageSrc: string,
    images: { url: string; description?: string }[] = [{ url: imageSrc }],
    index: number = 0
  ) => {
    // Dispatch custom event to open the fullscreen image modal in Index.tsx
    const event = new CustomEvent<ImageModalData>("openFullscreenImageModal", {
      detail: { images, initialIndex: index },
    });
    window.dispatchEvent(event);
  };

  const renderContent = useCallback(() => {
    // Render YouTube results if content is a structured JSON payload
    if (
      message.type === 'ai' &&
      typeof message.content === 'string'
    ) {
      try {
        const parsed = JSON.parse(message.content);
        if (parsed && parsed.mode === 'quiz' && Array.isArray(parsed.questions)) {
          return <QuizCardStack quiz={parsed} isDarkMode={isDarkMode} />;
        }
        if (parsed && parsed.__youtube && parsed.data) {
          return (
            <YouTubeResults
              data={parsed.data}
              query={parsed.query || ''}
              isDarkMode={isDarkMode}
            />
          );
        }
        if (parsed && parsed.__giphy && Array.isArray(parsed.gifs)) {
          const gifs = parsed.gifs.map((g: any, i: number) => {
            const url = typeof g === 'string' ? g : g.url;
            const title = (() => {
              try {
                const u = new URL(url);
                const name = (u.pathname.split('/').pop() || '').split('?')[0];
                return decodeURIComponent(name || 'GIF');
              } catch { return 'GIF'; }
            })();
            return { id: String(i), title, url, preview: url };
          }).filter((x: any) => x.url);
          return (
            <GiphyResults
              gifs={gifs}
              query={parsed.query || 'GIFs'}
              isDarkMode={isDarkMode}
            />
          );
        }
        if (parsed && parsed.__pixabay && Array.isArray(parsed.images)) {
          const images: Array<{ url: string; title?: string }> = parsed.images
            .map((img: any) => ({ url: typeof img === 'string' ? img : img.url, title: img.title }))
            .filter((i: any) => i.url);
          return (
            <div className={`my-3`}>
              <div className={`text-[11px] uppercase tracking-[0.35em] opacity-70 mb-2 ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                Results {parsed.query ? `for "${parsed.query}"` : ''}
              </div>
              <div className="columns-2 sm:columns-3 gap-2">
                {images.map((img, i) => (
                  <button
                    key={i}
                    type="button"
                    className={`mb-2 break-inside-avoid block overflow-hidden rounded-2xl border ${isDarkMode
                      ? 'bg-[#252525]/90 border-[#232323]'
                      : 'bg-gray-100/20 hover:bg-gray-200/30 border-gray-300'
                      }`}
                    onClick={() => handleImageClick(img.url, [{ url: img.url, description: img.title || 'Image' }], 0)}
                  >
                    <img
                      src={img.url}
                      alt={img.title || 'Image'}
                      loading="lazy"
                      className="w-full h-auto object-contain transition-transform duration-200 hover:scale-[1.01]"
                      onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                    />
                  </button>
                ))}
              </div>
            </div>
          );
        }
        if (parsed && parsed.__wiki_images && Array.isArray(parsed.images)) {
          const images: Array<{ url: string; title?: string }> = parsed.images
            .map((img: any) => ({ url: typeof img === 'string' ? img : img.url, title: img.title }))
            .filter((i: any) => i.url);
          return (
            <div className={`my-3`}>
              <div className={`text-[11px] uppercase tracking-[0.35em] opacity-70 mb-2 ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                Results {parsed.query ? `for "${parsed.query}"` : '(Wikimedia)'}
              </div>
              <div className="columns-2 sm:columns-3 gap-2">
                {images.map((img, i) => (
                  <button
                    key={i}
                    type="button"
                    className={`mb-2 break-inside-avoid block overflow-hidden rounded-2xl border ${isDarkMode
                      ? 'bg-[#252525]/90 border-[#232323]'
                      : 'bg-gray-100/20 hover:bg-gray-200/30 border-gray-300'
                      }`}
                    onClick={() => handleImageClick(img.url, [{ url: img.url, description: img.title || 'Image' }], 0)}
                  >
                    <img
                      src={img.url}
                      alt={img.title || 'Image'}
                      loading="lazy"
                      className="w-full h-auto object-contain transition-transform duration-200 hover:scale-[1.01]"
                      onError={(e) => {
                        console.error("Image failed to load:", img.url);
                        (e.currentTarget as HTMLImageElement).style.display = "none";
                      }}
                    />
                  </button>
                ))}
              </div>
            </div>
          );
        }
        if (parsed && parsed.__googleai) {
          // Extract citation sources from Google AI response
          const citationSources = parsed.data?.sources || parsed.data?.references || [];
          const googleAIContent = (() => {
            const fromBlocks = googleAITextBlocksToMarkdown(parsed.data?.text_blocks);
            return fromBlocks || parsed.text || "";
          })();

          return (
            <div className="flex flex-col gap-1">
              <FormattedAIResponse
                content={googleAIContent}
                isStreaming={isStreaming}
                onComplete={onStreamComplete}
                isDarkMode={isDarkMode}
                citationSources={citationSources}
              />
              {!isStreaming && <GoogleAIRichResults data={parsed.data || {}} isDarkMode={isDarkMode} />}
            </div>
          );
        }
        if (parsed && parsed.__vgen_video && parsed.videoId) {
          return (
            <VGenVideoStatus
              videoId={parsed.videoId}
              isDarkMode={isDarkMode}
            />
          );
        }
        // Structured simple agents (normal, neutral UI)
        if (parsed && parsed.type === 'calculator' && parsed.data) {
          const d = parsed.data;
          return <CalculatorCard data={d} isDarkMode={isDarkMode} />;
        }
        if (parsed && parsed.type === 'translate' && parsed.data) {
          const d = parsed.data;
          return <TranslatorCard data={d} isDarkMode={isDarkMode} />;
        }
        if (parsed && parsed.type === 'unit-converter' && parsed.data) {
          const d = parsed.data;
          return <UnitConverterCard data={d} isDarkMode={isDarkMode} />;
        }
        // Password Generator Card
        if (parsed && parsed.__password_generator && parsed.password) {
          return (
            <PasswordGeneratorCard
              initialPassword={parsed.password}
              initialOptions={parsed.options}
              isDarkMode={isDarkMode}
            />
          );
        }
        // QR Code Generator Card
        if (parsed && parsed.__qr_code_generator && parsed.qrUrl) {
          return (
            <QRCodeGeneratorCard
              text={parsed.text}
              qrUrl={parsed.qrUrl}
              isDarkMode={isDarkMode}
            />
          );
        }
        if (parsed && parsed.__googleWeather && parsed.data) {
          // Map Open-Meteo normalized payload to WeatherCard's WeatherData
          const d = parsed.data;
          const temp = d?.temperature?.degrees;
          const feels = d?.feelsLikeTemperature?.degrees;
          const humidity = d?.relativeHumidity ?? d?.humidity;
          const windValue = d?.wind?.speed?.value;
          const windUnit = d?.wind?.speed?.unit;
          const windKmh = typeof windValue === 'number'
            ? (windUnit === 'MILES_PER_HOUR' ? Math.round(windValue * 1.609) : Math.round(windValue))
            : 0;
          const pressure = d?.airPressure?.meanSeaLevelMillibars ?? 0;
          // Normalize visibility to km for WeatherCard display
          const visDist = d?.visibility?.distance;
          const visUnit = d?.visibility?.unit;
          const visibilityKm = typeof visDist === 'number'
            ? (visUnit === 'MILES' ? Math.round(visDist * 1.609) : Math.round(visDist))
            : null;
          const uvIndex = d?.uvIndex ?? undefined;
          const precipProb = d?.precipitation?.probability?.percent ?? undefined;
          const description = d?.condition?.text || 'Current conditions';
          const aqi = d?.extras?.airQuality?.european_aqi;

          // Compute a friendly location label: prefer reverse-geocoded name; else user's query if not coordinates; else fallback
          const candidateRaw = (typeof d?.locationName === 'string' && d.locationName.trim().length > 0)
            ? d.locationName
            : (typeof parsed?.query === 'string' ? parsed.query.trim() : '');
          const isCoordsInput = /^-?\d+\.?\d*,\s*-?\d+\.?\d*$/.test(candidateRaw);
          const locationLabel = candidateRaw && !isCoordsInput ? candidateRaw : 'Current Location';

          const weatherData = {
            location: locationLabel,
            temperature: typeof temp === 'number' ? Math.round(temp) : 0,
            description,
            humidity: typeof humidity === 'number' ? humidity : 0,
            windSpeed: windKmh,
            pressure: Math.round(pressure),
            feelsLike: typeof feels === 'number' ? Math.round(feels) : (typeof temp === 'number' ? Math.round(temp) : 0),
            visibility: typeof visibilityKm === 'number' ? Math.round(visibilityKm) : null,
            icon: '02d',
            backgroundImage: d?.backgroundImage || '',
            isDay: Boolean(d?.isDaytime),
            condition: d?.condition?.type || 'CLOUDS',
            uvIndex,
            airQuality: (typeof aqi === 'number' ? String(aqi) : undefined),
            tempMax: undefined,
            tempMin: undefined,
            rain: typeof precipProb === 'number' ? precipProb : undefined,
          } as any;

          return <WeatherCard data={weatherData} />;
        }
      } catch (_) {
        // not JSON; continue
      }
    }

    // Render LARA multi-agent results if available
    if ((message as any).isLARA && (message as any).laraResults) {
      return (
        <LARAResultsRenderer
          results={(message as any).laraResults}
          isDarkMode={isDarkMode}
        />
      );
    }
    // Render PPT Maker presentation if present
    if ((message as any).agentData?.ppt) {
      try {
        const ppt = (message as any).agentData.ppt;
        return <PPTMakerViewer ppt={ppt} isDarkMode={isDarkMode} />;
      } catch (_) {
        // Fallback to regular rendering if JSON parse fails
        return (
          <FormattedAIResponse
            content={message.content}
            isStreaming={isStreaming}
            onComplete={onStreamComplete}
            isDarkMode={isDarkMode}
          />
        );
      }
    }

    // Render Flowchart if present
    if ((message as any).agentData?.flowchart) {
      try {
        const flowSpec = (message as any).agentData.flowchart;
        return <FlowChartRenderer spec={flowSpec} isDarkMode={isDarkMode} />;
      } catch (_) {
        // Fallback to regular rendering if JSON parse fails
        return (
          <FormattedAIResponse
            content={message.content}
            isStreaming={isStreaming}
            onComplete={onStreamComplete}
            isDarkMode={isDarkMode}
          />
        );
      }
    }

    // Render weather card if weatherData is present (transform consolidated shape to WeatherData)
    if ((message as any).weatherData) {
      const wd: any = (message as any).weatherData;
      const transformed = wd?.current ? {
        location: wd.location || 'Unknown Location',
        temperature: Math.round(wd.current.temperature || wd.current.temp || 0),
        description: wd.current.description || wd.current.weather?.[0]?.description || 'No description',
        humidity: wd.current.humidity || 0,
        windSpeed: Math.round(wd.current.windSpeed || wd.current.wind_speed || 0),
        pressure: wd.current.pressure || 0,
        feelsLike: Math.round(wd.current.feelsLike || wd.current.feels_like || 0),
        visibility: wd.current.visibility || null,
        icon: wd.current.icon || wd.current.weather?.[0]?.icon || '01d',
        backgroundImage: wd.current.backgroundImage || 'https://images.unsplash.com/photo-1504608524841-42fe6f032b4b?w=400&h=300&fit=crop',
        isDay: wd.current.isDay !== undefined ? wd.current.isDay : true,
        condition: wd.current.condition || wd.current.weather?.[0]?.main || 'Clear',
        uvIndex: wd.uvIndex || wd.current.uvIndex,
        airQuality: wd.airQuality?.description,
        tempMax: wd.current.tempMax || wd.current.temp_max || wd.daily?.[0]?.temp_max || wd.daily?.[0]?.tempMax || (wd.current.temperature ? wd.current.temperature + 3 : null),
        tempMin: wd.current.tempMin || wd.current.temp_min || wd.daily?.[0]?.temp_min || wd.daily?.[0]?.tempMin || (wd.current.temperature ? wd.current.temperature - 5 : null),
        rain: wd.current.rain || wd.current.rain?.['1h']
      } : null;

      if (transformed) {
        return <WeatherCard data={transformed} />;
      }
      // Fallback: show raw data
      return (
        <div className="text-xs">
          {typeof wd === 'string' ? wd : JSON.stringify(wd, null, 2)}
        </div>
      );
    }
    // Wikipedia rich UI rendering
    if ((message as any).agentData?.wikipedia) {
      return (
        <WikipediaCard data={(message as any).agentData.wikipedia} isDarkMode={isDarkMode} />
      );
    }
    // Render web/search content only; sources will be rendered once below the bubble elsewhere
    if (message.searchResults) {
      return (
        <>
          {typeof message.content === 'string' && message.content.trim() !== '' && (
            <FormattedAIResponse content={message.content} isDarkMode={isDarkMode} />
          )}
        </>
      );
    }

    if (message.isImage) {
      return (
        <div className="flex justify-center">
          <LazyImage
            src={getOptimizedCloudinaryUrl(message.content, { width: 1200, crop: 'limit', quality: '90', format: 'auto', dpr: 'auto' })}
            alt="Generated content"
            className="max-w-full h-auto rounded-lg cursor-pointer hover:opacity-90 transition-opacity max-h-96 md:max-h-80 lg:max-h-72"
            onClick={() => handleImageClick(getOptimizedCloudinaryUrl(message.content, { width: 1600, crop: 'limit', quality: '90', format: 'auto', dpr: 'auto' }))}
            onError={(e) => {
              console.error("Image failed to load:", message.content);
              (e.currentTarget as HTMLImageElement).style.display = "none";
            }}
            rootMargin="400px"
            isDarkMode={isDarkMode}
            showPlaceholder={true}
          />
        </div>
      );
    }

    // Render Video Summary as React component when detected
    if (
      message.type === 'ai' &&
      typeof message.content === 'string' &&
      message.content.includes('data-video-summary')
    ) {
      try {
        const temp = document.createElement('div');
        temp.innerHTML = message.content;
        const root = temp.querySelector('[data-video-summary]') as HTMLElement | null;
        const link = temp.querySelector('[data-transcript-section] a[href]') as HTMLAnchorElement | null;
        const transcriptEl = temp.querySelector('.transcript-content') as HTMLElement | null;
        const body = temp.querySelector('.prose') as HTMLElement | null;
        const result: VSResult = {
          url: link?.getAttribute('href') || '',
          transcript: transcriptEl ? transcriptEl.textContent || '' : '',
          summary: body ? body.innerHTML : (typeof message.content === 'string' ? message.content : ''),
        };
        return <VideoSummaryCard result={result} isDarkMode={isDarkMode} />;
      } catch (e) {
        // Fallback to default renderer below
      }
    }

    // Render Search Results as React component when detected
    if (
      message.type === 'ai' &&
      typeof message.content === 'string' &&
      message.content.includes('data-search-results-react')
    ) {
      try {
        const temp = document.createElement('div');
        temp.innerHTML = message.content;
        const root = temp.querySelector('[data-search-results-react]') as HTMLElement | null;
        if (root) {
          const dataAttr = root.getAttribute('data-search-data');
          if (dataAttr) {
            const searchData: SearchResponse = JSON.parse(dataAttr.replace(/&apos;/g, "'"));
            return <SearchResultsCard data={searchData} isDarkMode={isDarkMode} />;
          }
        }
      } catch (e) {
        console.error('Failed to parse search results data:', e);
        // Fallback to default renderer below
      }
    }

    // Render raw HTML only when explicitly flagged as safe
    if (
      message.content &&
      typeof message.content === "string" &&
      (message.isHTML ||
        (message.type === "ai" &&
          (message.content.includes("<div") ||
            message.content.includes("<img") ||
            message.content.includes("<a")) &&
          !message.content.includes("```")))
    ) {
      let processedHtml = message.content;

      // Only process links if this is not a special HTML message (like our animation)
      if (!message.isHTML) {
        // Check if this is a search result message to avoid adding underlines
        const isSearchResult = message.selectedAgent?.name === "Google Search" ||
          message.content.includes('class="search-result"') ||
          message.content.includes('Google Search Results');

        // Process the HTML to style external links (but not for search results)
        if (!isSearchResult) {
          processedHtml = message.content.replace(
            /<a\s+(?:[^>]*?\s+)?href=(["'])(.*?)\1/g,
            (match, quote, url) => {
              // No special handling needed for Tavily links
              return `<a href=${quote}${url}${quote} style="text-decoration: underline; color: ${isDarkMode ? "white" : "#2563eb"}"`;
              return match;
            }
          );
        }
      }

      return (
        <div
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(processedHtml) }}
          className={`prose prose-sm max-w-none break-words ${isDarkMode ? "prose-invert" : ""
            }`}
          style={{
            color: isDarkMode ? "#ffffff" : "#000000",
            // For HTML messages, don't apply prose styles that might interfere
            ...(message.isHTML ? { all: "revert" } : {}),
            overflowWrap: 'anywhere',
            wordBreak: 'break-word',
          }}
        />
      );
    }

    // For AI responses, use formatted markdown rendering
    if (message.type === "ai") {
      // If content is empty but we're still streaming, show nothing (no loading dots)
      if ((!message.content || (typeof message.content === 'string' && message.content.trim() === "")) && isStreaming) {
        return null;
      }


      return (
        <div id={`m-${message.id}`} className="space-y-2" data-message-type="ai">
          {(() => {
            // Derive reasoning from explicit field or <think>...</think> in content
            let derivedReasoning: string | undefined = (message.reasoning as any) || (message as any).reasoning_content;
            if (!derivedReasoning && typeof message.content === 'string') {
              const thinkMatch = message.content.match(/<think>([\s\S]*?)<\/think>/i);
              if (thinkMatch && thinkMatch[1]) {
                derivedReasoning = thinkMatch[1].trim();
              }
            }
            return derivedReasoning;
          })() && (
              <div className="mb-0">
                <button
                  type="button"
                  onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                    if (e.detail === 0) return;
                    setShowReasoning(prev => !prev);
                  }}
                  className={`bg-transparent inline-flex items-center justify-center gap-1 text-xs font-medium mb-2 px-3 py-1.5 rounded-full transition-colors duration-150 focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 outline-none ${isDarkMode
                    ? "text-gray-200 hover:bg-[#404040]"
                    : "text-gray-700 hover:bg-gray-50"
                    }`}
                  aria-expanded={showReasoning}
                  aria-controls={`reasoning-${message.id}`}
                  tabIndex={-1}
                  onMouseDown={(e) => {
                    // Prevent the button from receiving focus on mouse interaction
                    e.preventDefault();
                  }}
                  onMouseUp={(e) => {
                    // Ensure the element does not remain focused after click
                    (e.currentTarget as HTMLButtonElement).blur();
                  }}
                  onPointerDown={(e) => {
                    // Prevent focus from pointer interactions across devices
                    e.preventDefault();
                  }}
                  onFocus={(e) => {
                    // If focused by any means, immediately blur to avoid focus ring
                    e.currentTarget.blur();
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      e.stopPropagation();
                    }
                  }}
                  onKeyUp={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      e.stopPropagation();
                    }
                  }}
                  style={{ outline: 'none', boxShadow: 'none' }}
                >
                  {showReasoning ? "Reasoning" : "Reasoning"}
                  <ChevronDown
                    className={`w-4 h-4 font-bold transition-transform duration-150  ${showReasoning ? "rotate-180" : "rotate-0"
                      }`}
                  />
                </button>
                <div
                  id={`reasoning-${message.id}`}
                  className={`grid transition-all duration-300 ${showReasoning ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
                    }`}
                >
                  <div className="overflow-hidden">
                    <div
                      className={`p-3 rounded-lg text-sm mb-3 ${isDarkMode
                        ? "bg-[#20202098] text-gray-300"
                        : "bg-blue-50 text-gray-700"
                        }`}
                    >
                      <FormattedAIResponse
                        content={((): string => {
                          // Provide the same derived reasoning content as above
                          if (message.reasoning) return message.reasoning as any;
                          if ((message as any).reasoning_content) return (message as any).reasoning_content as any;
                          if (typeof message.content === 'string') {
                            const m = message.content.match(/<think>([\s\S]*?)<\/think>/i);
                            if (m && m[1]) return m[1].trim();
                          }
                          return "";
                        })()}
                        isStreaming={false}
                        isDarkMode={isDarkMode}
                        citationSources={citationSources}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          <div className="text-sm">
            {message.selectedAgent === 'color-palette' ? (
              (() => {
                try {
                  const paletteData = typeof message.content === 'string'
                    ? JSON.parse(message.content)
                    : message.content;
                  return <ColorPaletteResponse palette={paletteData} isDarkMode={isDarkMode} />;
                } catch (e) {
                  // Fallback to regular rendering if JSON parse fails
                  return (
                    <FormattedAIResponse
                      content={message.content}
                      isStreaming={isStreaming}
                      onComplete={onStreamComplete}
                      isDarkMode={isDarkMode}
                    />
                  );
                }
              })()
            ) : message.selectedAgent === 'wikipedia' ? (
              (() => {
                try {
                  const wikiData = typeof message.content === 'string'
                    ? JSON.parse(message.content)
                    : message.content;
                  if (wikiData && wikiData.wikipedia) {
                    return <WikipediaCard data={wikiData.wikipedia} isDarkMode={isDarkMode} />;
                  }
                  // Fallback if no wikipedia data
                  return (
                    <FormattedAIResponse
                      content={message.content}
                      isStreaming={isStreaming}
                      onComplete={onStreamComplete}
                      isDarkMode={isDarkMode}
                    />
                  );
                } catch (e) {
                  // Fallback to regular rendering if JSON parse fails
                  return (
                    <FormattedAIResponse
                      content={message.content}
                      isStreaming={isStreaming}
                      onComplete={onStreamComplete}
                      isDarkMode={isDarkMode}
                    />
                  );
                }
              })()
            ) : message.model === 'deep-research' ? (
              <ParallelSearchResponse
                content={(() => {
                  // Remove <think>...</think> from displayed content if present
                  const content = message.content;
                  const thinkMatch = content.match(/<think>([\s\S]*?)<\/think>/i);
                  if (thinkMatch && thinkMatch[1]) {
                    return content.replace(/<think>.*?<\/think>/i, '');
                  }
                  return content;
                })()}
                isDarkMode={isDarkMode}
                sources={(message as any).parallelSources || []}
              />
            ) : (
              <FormattedAIResponse
                content={message.content}
                isStreaming={isStreaming}
                onComplete={onStreamComplete}
                isDarkMode={isDarkMode}
                // When in image mode, change text color
                className={isImageMode
                  ? (isDarkMode
                    ? "text-white/90 drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]"
                    : "text-black/90 drop-shadow-[0_1px_1px_rgba(255,255,255,0.8)]")
                  : undefined
                }
                citationSources={citationSources}
              />
            )}
          </div>
        </div>
      );
    }

    return (
      <>
        <div className="whitespace-pre-wrap break-words [overflow-wrap:anywhere]">{message.content}</div>
        {urls.length > 0 && (
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {urls.map((url, index) => (
              <div key={index} className="flex flex-col">
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-500 hover:text-blue-600 mb-2 break-all"
                >
                  {url}
                </a>
                <WebScreenshot
                  url={url}
                  size="md"
                  className="rounded-lg shadow-md hover:shadow-lg transition-shadow"
                />
              </div>
            ))}
          </div>
        )}
      </>
    );
  }, [message, isDarkMode, showReasoning, isStreaming, onStreamComplete]);

  const formatTimestamp = useCallback((timestampInput: Date | string) => {
    const timestamp = new Date(timestampInput);

    const time = timestamp.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });

    const date = timestamp.toLocaleDateString("en-US", {
      day: "numeric",
      month: "short",
    });

    return `${time}, ${date}`; // e.g. 2:29 PM, 14 Aug
  }, []);

  // Derived UI booleans to simplify JSX conditionals
  const hasContent = !!(message.content && (typeof message.content === 'string' ? message.content.trim() !== "" : true));
  const isWebSearchLoading = (message.model === "web-search" || message.model === "web search") && !message.searchResults;
  // Treat structured payloads (like weatherData) as renderable content even if content string is empty
  const hasStructuredContent = !!((message as any).weatherData || (message as any).laraResults || message.searchResults);
  const showMessageBubble = hasContent || hasStructuredContent || !!message.isImage || !!message.isHTML;

  // Detect quiz messages and extract topic name
  const { isQuizMessage, quizTopicName } = useMemo(() => {
    if (message.type === 'ai' && typeof message.content === 'string') {
      try {
        const parsed = JSON.parse(message.content);
        if (parsed && parsed.mode === 'quiz' && Array.isArray(parsed.questions)) {
          return { isQuizMessage: true, quizTopicName: parsed.title || parsed.topic || '' };
        }
      } catch { }
    }
    return { isQuizMessage: false, quizTopicName: '' };
  }, [message.content, message.type]);

  const canGenerateAudio = !message.isImage && !isQuizMessage && (hasContent || hasStructuredContent);
  const isLARAStreaming = (message as any).isLARA && isStreaming;
  const showActionRow = !isStreaming && !isQuizMessage && !(message as any).isStreaming && (hasContent || hasStructuredContent) && !isWebSearchLoading && !isLARAStreaming;

  if (message.type === "user") {
    const metaDoc = (() => {
      try {
        const ad = (message as any)?.agentData;
        if (ad?.attachedMedia && ad.attachedMedia.type === 'document') return ad.attachedMedia;
        return undefined;
      } catch {
        return undefined;
      }
    })();

    const docThumb = (message.attachedMedia && message.attachedMedia.type === "document")
      ? (message.attachedMedia as any)
      : (metaDoc as any);
    const hasDocThumb = !!docThumb;
    const primaryImage = (message.attachedMedia?.type === 'image') ? message.attachedMedia : undefined;
    const listImages = Array.isArray(message.attachedMediaList)
      ? message.attachedMediaList.filter((m) => m?.type === 'image' && typeof m.url === 'string' && m.url)
      : [];
    const dedupedImages = (() => {
      const seen = new Set<string>();
      const out: AttachedMedia[] = [];
      const push = (m?: AttachedMedia) => {
        if (!m?.url) return;
        if (seen.has(m.url)) return;
        seen.add(m.url);
        out.push(m);
      };
      push(primaryImage as any);
      listImages.forEach((m) => push(m));
      return out;
    })();

    const thumbItems = [
      ...dedupedImages.map((m) => ({ kind: 'image' as const, media: m })),
      ...(hasDocThumb ? [{ kind: 'document' as const, media: docThumb }] : []),
    ].slice(0, 7);

    return (
      <div
        id={`m-${message.id}`}
        className="flex justify-end group mt-2"
      >
        <div className="flex flex-col items-end max-w-[85vw]">
          {thumbItems.length > 0 && (
            <div
              className={`relative overflow-visible rounded-xl text-sm py-1 pl-1 max-w-full bg-transparent ${isDarkMode
                ? "text-gray-100"
                : "text-gray-900"
                }`}
            >
              {dedupedImages.length > 0 && (
                <div
                  className="w-full columns-2 sm:columns-3 gap-2 [column-fill:_balance]"
                  style={{ direction: 'rtl' }}
                >
                  {dedupedImages.slice(0, 7).map((media, idx) => (
                    <button
                      key={`thumb-image-${idx}`}
                      type="button"
                      className="group/thumbnail overflow-hidden text-left block w-full max-w-[140px] max-h-[140px] mb-2 break-inside-avoid cursor-pointer transition-all"
                      style={{ direction: 'ltr' }}
                      onClick={() => {
                        const gallery = dedupedImages.map((i, imgIdx) => ({ url: i.url, description: `Image ${imgIdx + 1}` }));
                        const first = getOptimizedCloudinaryUrl(media.url, { width: 1600, crop: 'limit', quality: 'auto', format: 'auto', dpr: 'auto' });
                        handleImageClick(first, gallery.length ? gallery : undefined, gallery.findIndex((g) => g.url === media.url));
                      }}
                      draggable={false}
                      onDragStart={(event) => event.preventDefault()}
                    >
                      <LazyImage
                        src={getOptimizedCloudinaryUrl(media.url, { width: 520, crop: 'limit', quality: 'auto', format: 'auto', dpr: 'auto' })}
                        alt={media.name || "Attached image"}
                        className={`w-full h-auto object-contain rounded-lg border shadow-sm hover:shadow-md 
                          ${isDarkMode ? 'bg-[#1f1f1f] border-[#3a3a3a] hover:border-[#5a5a5a]' : 'bg-white border-gray-200 hover:border-gray-300'}`}
                        draggable={false}
                        onDragStart={(event) => event.preventDefault()}
                        onError={(e) => {
                          console.error("Attachment preview failed to load:", media?.url);
                          (e.currentTarget as HTMLImageElement).style.display = "none";
                        }}
                        rootMargin="300px"
                        isDarkMode={isDarkMode}
                        showPlaceholder={true}
                      />
                    </button>
                  ))}
                </div>
              )}

              {hasDocThumb && (
                <div className="mt-2 flex justify-end" style={{ direction: 'ltr' }}>
                  <button
                    key={`thumb-doc-0`}
                    type="button"
                    className={`group/thumbnail text-left block cursor-pointer transition-all rounded-lg border flex flex-col justify-between gap-2.5 overflow-hidden px-2.5 py-2 shadow-sm w-[120px] h-[120px] min-w-[120px] ml-auto ${isDarkMode ? 'bg-[#1f1f1f] border-[#3a3a3a]' : 'bg-white border-gray-200'} ${isDarkMode ? 'hover:border-[#5a5a5a]' : 'hover:border-gray-300'} hover:shadow-md`}
                    onClick={() => {
                      const images = (message.attachedMediaList || []).map((i, pageIdx) => ({ url: i.url, description: `Page ${pageIdx + 1}` }));
                      const first = images[0]?.url || docThumb?.url;
                      if (!first) return;
                      handleImageClick(first, images.length > 0 ? images : [{ url: first, description: 'Page 1' }], 0);
                    }}
                  >
                    <div className="flex flex-col gap-1 min-h-0">
                      <h3 className={`text-[12px] break-words line-clamp-3 ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                        {(() => {
                          const raw = String(docThumb?.name || 'Document');
                          const max = 20;
                          if (raw.length <= max) return raw;
                          return `${raw.slice(0, max)}...`;
                        })()}
                      </h3>
                      <p className={`text-[10px] line-clamp-1 break-words ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        {(() => {
                          const pc = Number((docThumb as any)?.pageCount || (message.attachedMediaList?.length || 0));
                          return pc > 0 ? `${pc} pages` : 'Document';
                        })()}
                      </p>
                    </div>

                    <div>
                      <div className="relative flex flex-row items-center gap-1 justify-between">
                        <div className="flex flex-row gap-1 shrink min-w-0">
                          <div className={`min-w-0 h-[18px] flex flex-row items-center justify-center gap-0.5 px-1 border shadow-sm rounded font-medium ${isDarkMode ? 'border-[#3a3a3a] bg-[#2a2a2a]/70 text-gray-200' : 'border-gray-200 bg-white/70 text-gray-700'}`}>
                            <p className="uppercase truncate text-[11px] leading-[13px]">
                              {(() => {
                                const explicit = String((docThumb as any)?.originalExt || '').toLowerCase();
                                if (explicit) return explicit;
                                const name = String(docThumb?.name || '').toLowerCase();
                                const ext = name.includes('.') ? name.split('.').pop() : '';
                                return ext || 'file';
                              })()}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </button>
                </div>
              )}
              {message.attachedMedia && message.attachedMedia.type !== "image" && message.attachedMedia.type !== "document" && (
                <div className="mt-1 text-sm opacity-75 text-right">
                  📎 {message.attachedMedia.name}
                </div>
              )}
            </div>
          )}

          {showMessageBubble && (
            <div
              className={`relative rounded-xl rounded-br-[4px] text-sm px-4 py-2 max-w-full transition-all duration-200 ${isDarkMode
                ? "bg-[#1c1c1c] border-gray-600 text-gray-100"
                : "bg-gray-100 text-gray-900"
                } ${(!isUserExpanded && needsUserTruncation) ? 'max-h-[150px] overflow-hidden' : 'overflow-visible'}`}
            >
              <div
                ref={userContentRef}
                className="message-content select-text"
                style={{ WebkitUserSelect: 'text', WebkitTouchCallout: 'default', userSelect: 'text' }}
              >
                {renderContent()}
              </div>
              {!isUserExpanded && needsUserTruncation && (
                <div
                  className={`absolute bottom-0 left-0 right-0 h-10 bg-gradient-to-t pointer-events-none ${
                    isDarkMode ? 'from-[#1c1c1c] to-transparent' : 'from-gray-100 to-transparent'
                  }`}
                />
              )}
            </div>
          )}
          {needsUserTruncation && (
            <button
              type="button"
              onClick={() => setIsUserExpanded(!isUserExpanded)}
              className={`mt-1 flex items-center gap-0.5 text-xs font-semibold select-none ${
                isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-black'
              } transition-colors`}
            >
              {isUserExpanded ? 'Show less' : 'Show more'}
              <ChevronDown
                className={`w-3.5 h-3.5 transition-transform ${isUserExpanded ? 'rotate-180' : ''}`}
              />
            </button>
          )}
          {onEdit && (
            <div className={`mt-0 flex items-center gap-2 transition-opacity duration-200 opacity-0 group-hover:opacity-100`}>
              {message.timestamp && (
                <span
                  className={`mt-1 text-[11px] leading-none select-none ${isImageMode
                    ? (isDarkMode
                      ? "text-white/80 drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]"
                      : "text-black/80 drop-shadow-[0_1px_1px_rgba(255,255,255,0.8)]")
                    : (isDarkMode ? "text-gray-400" : "text-gray-500")
                    }`}
                >
                  {new Date(message.timestamp as any).toLocaleTimeString("en-US", {
                    hour: "numeric",
                    minute: "2-digit",
                    hour12: true,
                  })}
                </span>
              )}
               <Button
                variant="ghost"
                size="sm"
                onClick={handleCopyClick}
                className={`relative h-6 w-6 group p-1 transition-colors mt-1 ${isImageMode ?
                  (isDarkMode ? "text-white/90 drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]" : "text-black/90 drop-shadow-[0_1px_1px_rgba(255,255,255,0.8)]") :
                  (isDarkMode
                    ? "text-gray-300 hover:text-white hover:bg-[#424242]"
                    : "text-gray-600 hover:text-black hover:bg-[#e3e3e3]"
                  )
                  }`}
              >
                {copiedMessageId === message.id ? (
                  <Check className="w-3 h-3 text-green-500 animate-in fade-in zoom-in-75" />
                ) : (
                  <ClipboardIcon className="w-3 h-3" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onEdit(message.content)}
                className={`relative h-6 w-6 group p-1 transition-colors mt-1 ${isImageMode ?
                  (isDarkMode ? "text-white/90 drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]" : "text-black/90 drop-shadow-[0_1px_1px_rgba(255,255,255,0.8)]") :
                  (isDarkMode
                    ? "text-gray-300 hover:text-white hover:bg-[#424242]"
                    : "text-gray-600 hover:text-black hover:bg-[#e3e3e3]"
                  )
                  }`}
              >
                <EditIcon className="w-3 h-3" size="0.75rem" />
              </Button>
            </div>
          )}

          {message.branchedFrom && (
            <div className="flex items-center gap-3 mt-3 mb-1 text-xs select-none">
              <div
                className={
                  `flex-1 h-px w-full rounded-full bg-gradient-to-r from-transparent ${isDarkMode ? 'via-white/25' : 'via-primary/35'
                  } to-transparent`
                }
              />
              <div className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                Branched from <span className="font-semibold">{message.branchedFrom.summary?.trim() || 'previous message'}</span>
              </div>
              <div
                className={
                  `flex-1 h-px w-full rounded-full bg-gradient-to-r from-transparent ${isDarkMode ? 'via-white/25' : 'via-primary/35'
                  } to-transparent`
                }
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  // AI message
  return (
    <>
      <div id={`m-${message.id}`} className="flex justify-start mt-0 mb-4">
        <div className="flex flex-col items-start max-w-3xl w-full">
          {/* Model/Agent name at top left - only show when not streaming and content exists and not web search mode and not during search results */}
          {/* Hide LARA label when streaming */}
          {displayModelName && !isStreaming && hasContent && !isLARAStreaming && !(message as any).multiBatchId && !(message as any).isStreaming && ((displayModelName || '').toLowerCase() !== 'quiz' || isQuizMessage) && (
            <div
              className={`text-sm font-normal select-none animate-fadeInUp mb-[8px] ${isImageMode ?
                (isDarkMode ? "text-white/90 drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]" : "text-black/90 drop-shadow-[0_1px_1px_rgba(255,255,255,0.8)]") :
                (isDarkMode ? "text-white" : "text-[black]")
                }`}
              style={{
                animation: 'fadeInUp 0.4s ease-out forwards',
                opacity: 0
              }}
            >
              {(() => {
                // Prefer selected agent details if available
                const agent = message.selectedAgent?.id
                  ? ALL_AGENTS.find(a => a.id === message.selectedAgent.id)
                  : (message.selectedAgent?.name
                    ? ALL_AGENTS.find(a => a.name.toLowerCase() === String(message.selectedAgent.name).toLowerCase())
                    : undefined);
                const logo = agent?.icon || getModelLogo(displayModelName, isDarkMode);
                const modelKey = (message.model || displayModelName || "").toLowerCase();
                const name = isQuizMessage && quizTopicName
                  ? quizTopicName
                  : agent?.name || (
                    modelKey === "glm"
                      ? "ChatGPT"
                      : (modelKey === "web-search" || modelKey === "web search")
                        ? "Web Search"
                        : modelKey === "deep-research"
                          ? "Deep research"
                          : modelKey === "chitra-ai"
                            ? "Chitra"
                            : getDisplayName(displayModelName, message.selectedAgent?.name)
                  );

                const isPerplexity = (displayModelName || '').toLowerCase() === 'perplexity' || (message.model || '').toLowerCase() === 'perplexity';
                const isKimi = (displayModelName || '').toLowerCase() === 'kimi' || (message.model || '').toLowerCase() === 'kimi';
                const isGoogleAI = ['google ai', 'googleai'].includes((displayModelName || '').toLowerCase()) || ['google ai', 'googleai'].includes((message.model || '').toLowerCase());
                const isChitraMode = modelKey === 'chitra-ai';
                const isWebSearchMode = modelKey === 'web-search' || modelKey === 'web search';
                const isDeepResearchMode = modelKey === 'deep-research';
                return (
                  <div className="flex items-center gap-2">
                    {isPerplexity ? (
                      <PerplexityIcon className="w-5 h-5" />
                    ) : isKimi ? (
                      <KimiIcon className={`w-5 h-5 ${isDarkMode ? 'text-white' : 'text-black'}`} />
                    ) : isGoogleAI ? (
                      <GoogleAIIcon className="w-5 h-5" />
                    ) : isChitraMode ? (
                      <CreateImageIcon className={`w-5 h-5 ${isDarkMode ? 'text-white' : 'text-black'}`} />
                    ) : isWebSearchMode ? (
                      <Globe className="w-5 h-5" />
                    ) : isDeepResearchMode ? (
                      <Atom className="w-5 h-5" />
                    ) : (displayModelName || '').toLowerCase().includes('claude') ? (
                      <ClaudeIcon className="w-5 h-5 text-[#D97757]" />
                    ) : (displayModelName || '').toLowerCase().includes('gpt') ? (
                      <GPTIcon className={`h-5 w-5 ${isDarkMode ? 'text-white' : 'text-black'}`} />
                    ) : (displayModelName || '').toLowerCase().includes('gemini') ? (
                      <GeminiIcon className="w-5 h-5" />
                    ) : (displayModelName || '').toLowerCase().includes('llama') ? (
                      <LlamaIcon className={`w-5 h-5 ${isDarkMode ? 'text-white' : 'text-black'}`} />
                    ) : (displayModelName || '').toLowerCase().includes('deepseek') ? (
                      <DeepseekIcon className="w-5 h-5" />
                    ) : (displayModelName || '').toLowerCase().includes('qwen') ? (
                      <QwenIcon className="w-5 h-5" />
                    ) : (
                      logo && (
                        <img
                          src={logo}
                          alt={getDisplayName(displayModelName, message.selectedAgent?.name)}
                          className="w-5 h-5 rounded-full object-cover"
                        />
                      )
                    )}
                    <span className={`${isImageMode ?
                      (isDarkMode ? "text-white/90 drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]" : "text-black/90 drop-shadow-[0_1px_1px_rgba(255,255,255,0.8)]") :
                      (isDarkMode ? "text-white" : "text-[black]")
                      }`}>{name}</span>
                  </div>
                );
              })()}
            </div>
          )}

          {/* Only show message bubble when content exists and not streaming empty content */}
          {showMessageBubble && (
            <div
              className={`rounded-xl pl-2 pr-3 xs:pr-4 py-2 ${(message as any).agentData?.flowchart ? 'w-full' : 'w-[max-content]'
                } max-w-full overflow-x-auto thin-scrollbar transition-colors duration-300 
              ${backgroundType === 'image'
                  ? (isDarkMode ? "bg-transparent text-white" : "bg-gray-50 text-gray-900")
                  : (isDarkMode ? "bg-transparent text-white" : "bg-transparent text-gray-900")}
                  `}
            >
              <div
                className="message-content select-text break-words [overflow-wrap:anywhere]"
                style={{ WebkitUserSelect: 'text', WebkitTouchCallout: 'default', userSelect: 'text' }}
              >
                {renderContent()}
              </div>
            </div>
          )}

          {/* Render sources (favicons + links) when attached search results exist */}
          {message.searchResults && (
            <SearchResults
              results={message.searchResults as any}
              isDarkMode={isDarkMode}
              hideSourcesButton
            />
          )}

          {message.branchedFrom && (
            <div className="flex items-center gap-3 mt-3 mb-1 text-xs select-none w-full">
              <div
                className={
                  `flex-1 h-px w-full rounded-full bg-gradient-to-r from-transparent ${isDarkMode ? 'via-white/25' : 'via-primary/35'
                  } to-transparent`
                }
              />
              <div className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                Branched from <span className="font-semibold">{message.branchedFrom.summary?.trim() || 'previous message'}</span>
              </div>
              <div
                className={
                  `flex-1 h-px w-full rounded-full bg-gradient-to-r from-transparent ${isDarkMode ? 'via-white/25' : 'via-primary/35'
                  } to-transparent`
                }
              />
            </div>
          )}

          {/* Action buttons and timestamp - only show when not streaming and content exists and not during web search loading */}
          {showActionRow && !hideActions && (
            <div
              className="flex justify-between items-center w-full mt-1 mb-2.5"
              style={{
                animation: 'fadeInUp 0.4s ease-out forwards',
                opacity: 0
              }}
            >
              <div className="flex items-center gap-0.5">
                {message.type === 'ai' && (
                  <>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleCopyClick}
                      disabled={isCopying}
                      className={`relative h-6.5 w-6.5 group text-xs p-1.5 transition-all duration-300 ${isDarkMode
                        ? "text-white hover:bg-[#424242] hover:border-gray-600"
                        : "text-black hover:bg-[#e3e3e3] hover:border-gray-300"
                        }`}
                    >
                      {isCopying ? (
                        <div className="w-3 h-3 border border-current rounded-full animate-spin border-t-transparent" />
                      ) : copiedMessageId === message.id ? (
                        <Check className="w-2 h-2 text-green-500 animate-in fade-in-50 zoom-in-95" />
                      ) : (
                        <ClipboardIcon className="w-3 h-3" />
                      )}
                      <div className="absolute left-8 -translate-x-1/2 top-full mt-1 px-2 py-1 text-xs rounded-md bg-black/80 text-white whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                        {copiedMessageId === message.id ? 'Copied!' : 'Copy'}
                      </div>
                    </Button>
                    {!message.isImage && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          if (!canGenerateAudio || isGeneratingAudio) return;
                          if (isPlayingAudio) {
                            try {
                              const audioManager = AudioManager.getInstance();
                              audioManager.stopAudio();
                              window.dispatchEvent(
                                new CustomEvent("audioManagerUpdate", {
                                  detail: { action: "stop", messageId: message.id },
                                })
                              );
                              window.dispatchEvent(
                                new CustomEvent("audioManagerUpdate", {
                                  detail: { action: "ended", messageId: message.id },
                                })
                              );
                            } catch { }
                            setIsPlayingAudio(false);
                            return;
                          }
                          handleListen();
                        }}
                        disabled={!canGenerateAudio || isGeneratingAudio}
                        className={`relative h-6.5 w-6.5 group text-xs p-1.5 transition-colors ${isDarkMode
                          ? "text-white hover:bg-[#424242] hover:border-gray-600"
                          : "text-black hover:bg-[#e3e3e3] hover:border-gray-300"
                          }`}
                      >
                        {isGeneratingAudio ? (
                          <div className="w-3 h-3 border border-current rounded-full animate-spin border-t-transparent" />
                        ) : isPlayingAudio ? (
                          <div className="h-3 w-3 bg-current rounded-sm"></div>
                        ) : (
                          <VolumeIcon className="w-3 h-3" />
                        )}
                        <div className="absolute left-1/2 -translate-x-1/2 top-full mt-1 px-2 py-1 text-xs rounded-md bg-black/80 text-white whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                          {isPlayingAudio ? 'Close' : 'Play audio'}
                        </div>
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        if (reaction === null) {
                          setReaction("like");
                          setShowDislike(false);
                        }
                      }}
                      disabled={reaction === "like"}
                      className={`relative h-6.5 w-6.5 group text-xs p-1.5 transition-all duration-300 disabled:opacity-100  ${isDarkMode
                        ? "text-white hover:bg-[#424242] hover:border-gray-600"
                        : "text-black hover:bg-[#e3e3e3] hover:border-gray-300"
                        }`}
                      style={{
                        opacity: reaction === "like" ? 1 : undefined,
                        display: showLike ? 'block' : 'none',
                        transform: showLike ? 'scale(1)' : 'scale(0.8)',
                        transition: 'transform 0.2s ease',
                        position: 'relative',
                        zIndex: 1
                      }}
                    >
                      <FontAwesomeIcon
                        icon={reaction === "like" ? fasThumbsUp : farThumbsUp}
                        className={`w-3 h-3 ${isDarkMode ? "text-white" : "text-black"}`}
                      />
                      <div className="absolute left-1/2 -translate-x-1/2 top-full mt-1 px-2 py-1 text-xs rounded-md bg-black/80 text-white whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                        Good response
                      </div>
                    </Button>
                    <div className="relative">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          if (reaction === null) {
                            setReaction("dislike");
                            setShowLike(false);
                            // Dispatch event to show feedback form in Index.tsx with message context
                            window.dispatchEvent(new CustomEvent("showFeedbackForm", {
                              detail: {
                                message: typeof message.content === 'string' ? message.content : JSON.stringify(message.content),
                                isDislikeTrigger: true
                              }
                            }));
                          }
                        }}
                        disabled={reaction === "dislike"}
                        className={`relative h-6.5 w-6.5 group z-10 text-xs p-1.5 transition-all duration-300 disabled:opacity-100 ${isDarkMode
                          ? "text-white hover:bg-[#424242] hover:border-gray-600"
                          : "text-[#5D5D5D] hover:bg-[#e3e3e3] hover:border-gray-300"
                          }`}
                        style={{
                          opacity: reaction === "dislike" ? 1 : undefined,
                          display: showDislike ? 'block' : 'none',
                          transform: showDislike ? 'scale(1)' : 'scale(0.8)',
                          transition: 'transform 0.2s ease',
                          position: 'relative',
                          zIndex: 1
                        }}
                      >
                        <FontAwesomeIcon
                          icon={reaction === "dislike" ? fasThumbsDown : farThumbsDown}
                          className={`w-3 h-3 ${isDarkMode ? "text-white" : "text-black"}`}
                        />
                        <div className="absolute left-1/2 -translate-x-1/2 top-full mt-1 px-2 py-1 text-xs rounded-md bg-black/80 text-white whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                          Bad response
                        </div>
                      </Button>

                    </div>
                    <div className="relative z-[100]" ref={moreAnchorRef}>
                      <Button
                        variant="ghost"
                        size="sm"
                        onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); setShowMoreDropdown((prev) => !prev); }}
                        className={`relative h-6.5 w-6.5 group text-xs p-1.5 transition-colors ${isDarkMode
                          ? "text-white hover:text-gray-200 hover:bg-[#424242] hover:border-gray-600"
                          : "text-black hover:bg-[#e3e3e3] hover:border-gray-300"
                          }`}
                      >
                        <MoreHorizontal className="w-3 h-3" />
                        <div className="absolute left-1/2 -translate-x-1/2 top-full mt-1 px-2 py-1 text-xs rounded-md bg-black/80 text-white whitespace-nowrap opacity-0 sm:group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                          More
                        </div>
                      </Button>

                      {/* Custom Dropdown (portal) */}
                      {showMoreDropdown && createPortal(
                        <div
                          ref={dropdownRef}
                          className={`${isDarkMode ? 'bg-[#1c1c1c] border-[#2e2e2e]' : 'bg-white border-gray-200'} fixed w-48 rounded-2xl shadow-lg border z-[999999]`}
                          style={{
                            top: moreCoords.top,
                            left: moreCoords.left,
                            opacity: moreReady ? 1 : 0,
                            pointerEvents: moreReady ? 'auto' : 'none',
                            animation: moreReady ? `${morePlaceTop ? 'fadeInDown' : 'fadeInUp'} 0.18s ease-out both` : 'none'
                          }}
                        >
                          <div className="p-2 rounded-2xl space-y-0">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setShowMoreDropdown(false);
                                shareMessage();
                              }}
                              className={`w-full justify-start gap-2 text-sm rounded-xl ${isDarkMode
                                ? "text-white hover:text-gray-200 hover:bg-[#2e2e2e]"
                                : "text-black hover:text-gray-900 hover:bg-gray-200"
                                }`}
                            >
                              <Share2 className="w-3 h-3" />
                              <span>Share</span>
                            </Button>

                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={async () => {
                                setIsBranching(true);
                                setShowMoreDropdown(false);

                                // Small delay for smooth UI feedback
                                await new Promise(resolve => setTimeout(resolve, 100));

                                try {
                                  await onBranch?.(message.id);
                                } finally {
                                  setIsBranching(false);
                                }
                              }}
                              disabled={isBranching}
                              className={`w-full justify-start gap-2 text-sm transition-all duration-200 rounded-xl ${isDarkMode
                                ? "text-white hover:text-gray-200 hover:bg-[#2e2e2e]"
                                : "text-black hover:text-gray-900 hover:bg-gray-200"
                                } ${isBranching ? 'opacity-75 cursor-not-allowed' : ''}`}
                            >
                              {isBranching ? (
                                <div className="w-3 h-3 border border-current rounded-full animate-spin border-t-transparent" />
                              ) : (
                                <GitBranch className="w-3 h-3" />
                              )}
                              <span>{isBranching ? 'Creating branch...' : 'Branch in new chat'}</span>
                            </Button>

                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setShowMoreDropdown(false);
                                handleOpenInDoc();
                              }}
                              className={`w-full justify-start gap-2 text-sm transition-all duration-200 rounded-xl ${isDarkMode
                                ? "text-white hover:text-gray-200 hover:bg-[#2e2e2e]"
                                : "text-black hover:text-gray-900 hover:bg-gray-200"
                                }`}
                              disabled={!hasContent && !hasStructuredContent}
                            >
                              <FileText className="w-3 h-3" />
                              <span>Open in Doc</span>
                            </Button>

                            {message.type === 'ai' && message.content && onAskOtherAI && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setShowMoreDropdown(false);
                                  setShowAskOtherAI(true);
                                }}
                                className={`w-full justify-start gap-2 text-sm transition-all duration-200 rounded-xl ${isDarkMode
                                  ? "text-white hover:text-gray-200 hover:bg-[#2e2e2e]"
                                  : "text-black hover:text-gray-900 hover:bg-gray-200"
                                  }`}
                              >
                                <img
                                  src="/ai-svg-oreog.svg"
                                  alt="AI"
                                  className={`w-4 h-4 ${isDarkMode ? "opacity-80" : "opacity-90"}`}
                                />
                                <span>Ask Other AI</span>
                              </Button>
                            )}

                            <div className={`pt-2 mt-2 text-xs ${isDarkMode ? 'border-gray-700 text-gray-400' : 'border-gray-200 text-gray-600'
                              }`}>
                              <div className="px-2">{formatTimestamp(message.timestamp)}</div>
                            </div>
                          </div>
                        </div>,
                        document.body
                      )}

                      {/* Ask Other AI Dropdown (portal) */}
                      {showAskOtherAI && createPortal(
                        <div
                          ref={askAIDropdownRef}
                          className={`${isDarkMode ? 'bg-[#1c1c1c] border-[#2e2e2e]' : 'bg-white border-gray-200'} fixed w-56 rounded-2xl shadow-lg border z-[999999]`}
                          style={{
                            top: askCoords.top,
                            left: askCoords.left,
                            animation: `${askPlaceTop ? 'fadeInDown' : 'fadeInUp'} 0.18s ease-out both`,
                            opacity: askCoords.top === 0 && askCoords.left === 0 ? 0 : 1
                          }}
                        >
                          <div className="p-2 rounded-2xl space-y-1">
                            <div className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'} px-3 py-2 text-xs font-medium`}>
                              Select AI Model
                            </div>
                            {[
                              { id: 'chat gpt', name: 'ChatGPT', logo: 'gpt' },
                              { id: 'deepseek', name: 'DeepSeek', logo: deepseekLogo },
                              { id: 'gemini', name: 'Gemini', logo: geminiLogo },
                              { id: 'llama', name: 'LLaMA', logo: llamaLogo },
                              { id: 'qwen', name: 'Qwen', logo: qwenLogo },
                              { id: 'kimi', name: 'Kimi', logo: undefined as any },
                              { id: 'claude', name: 'Claude', logo: claudeLogo },
                              { id: 'lara', name: 'LARA', logo: '/ai-svg-oreog.svg' },
                            ].map((model) => (
                              <div key={model.id} className="relative">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    if (model.id === 'claude') {
                                      setExclusiveAskHover('claude');
                                      return;
                                    }
                                    setShowAskOtherAI(false);
                                    if (onAskOtherAI) onAskOtherAI(message.id, model.id);
                                  }}
                                  onMouseEnter={() => {
                                    if (model.id === 'claude') {
                                      setExclusiveAskHover('claude');
                                      setExclusiveAskSticky(false);
                                    }
                                  }}
                                  onMouseLeave={() => {
                                    if (!exclusiveAskSticky && exclusiveAskHover === 'claude') setExclusiveAskHover(null);
                                  }}
                                  onMouseDown={() => {
                                    if (model.id === 'claude') {
                                      setExclusiveAskHover('claude');
                                      setExclusiveAskSticky(true);
                                    }
                                  }}
                                  onTouchStart={() => {
                                    if (model.id === 'claude') {
                                      setExclusiveAskHover('claude');
                                      setExclusiveAskSticky(true);
                                    }
                                  }}
                                  className={`w-full justify-start gap-2 text-sm rounded-xl ${isDarkMode
                                    ? 'text-white hover:text-gray-200 hover:bg-[#2e2e2e]'
                                    : 'text-black hover:text-gray-900 hover:bg-gray-200'
                                    } ${model.id === 'claude' ? 'cursor-not-allowed opacity-80' : ''}`}
                                >
                                  {model.id === 'perplexity' ? (
                                    <PerplexityIcon className="w-5 h-5" />
                                  ) : model.id === 'kimi' ? (
                                    <KimiIcon className="w-5 h-5" />
                                  ) : model.id === 'chat gpt' ? (
                                    <GPTIcon className={`w-5 h-5 ${isDarkMode ? 'text-white' : 'text-black'}`} />
                                  ) : (
                                    <img
                                      src={model.logo}
                                      alt={model.name}
                                      className="w-5 h-5 rounded-full object-cover"
                                      onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                                    />
                                  )}
                                  <span>{model.name}</span>
                                </Button>

                                {model.id === 'claude' && exclusiveAskHover === 'claude' && (
                                  <div
                                    ref={exclusiveAskPanelRef}
                                    className={`absolute left-2 right-2 top-full mt-2 z-[1000000] rounded-2xl p-3 border w-[max-content] gap-2 flex flex-row bg-white border-gray-200 text-gray-800 shadow-lg`}
                                    style={{ animation: 'fadeInUp 0.18s ease-out both' }}
                                  >
                                    <div className="text-sm font-medium text-center">Only for Exclusive members</div>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>,
                        document.body
                      )}
                    </div>
                  </>
                )}

                {message.searchResults?.results?.length ? (
                  <SearchSourcesButton
                    results={message.searchResults as any}
                    isDarkMode={isDarkMode}
                    variant="compact"
                    className="ml-1"
                    isImageMode={isImageMode}
                  />
                ) : null}
              </div>

            </div>
          )}

          {/* Mini audio bar under the message when this message's audio is active */}
          <MiniAudioBar messageId={message.id} isDarkMode={isDarkMode} />
        </div>
      </div>

      <ScreenshotModal
        isOpen={modalState.isOpen}
        onClose={() => setModalState((prev) => ({ ...prev, isOpen: false }))}
        imageUrl={modalState.imageUrl}
        title={modalState.title}
      />


    </>
  );
};